// ABOUTME: Steps two and three of applying: the PRO CV note for a talent without one (B2), and the last check (B3):
// ABOUTME: the job, the week sent, the start, the location sharing a geofence check needs, and the process ahead.
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Text } from 'components/Text';
import type { AvailabilityDay } from 'modules/availability/types';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import type { JobListing } from '../../types';
import { formatDay, formatMoney, hoursLabel } from '../../utils';
import { ProcessSteps } from '../ListingDetailSections';
import { daysWithTime, isoDayOf } from './weekDraft';

export const CvStep = () => (
  <Box data-testid="apply-cv-step" sx={{ display: 'grid', gap: 1.5 }}>
    <Text variant="subtitle1" color="text.primary" sx={{ fontWeight: 700 }}>
      {getTrans('jobs.apply.cv.title')}
    </Text>
    <Text variant="body2" color="text.primary" component="p">
      {getTrans('jobs.apply.cv.body')}
    </Text>
  </Box>
);

const Row = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      py: 0.75,
      borderTop: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Text variant="body2" color="text.secondary">
      {label}
    </Text>
    <Text
      variant="body2"
      color="text.primary"
      sx={{ fontWeight: 600, textAlign: 'right' }}
    >
      {value}
    </Text>
  </Box>
);

export const ReviewStep = ({
  listing,
  days,
  weeklyMinutes,
  startFrom,
  needsLocation,
  locationConfirmed,
  onLocationConfirmed
}: {
  listing: JobListing;
  days: AvailabilityDay[];
  weeklyMinutes: number;
  startFrom: string;
  needsLocation: boolean;
  locationConfirmed: boolean;
  onLocationConfirmed: (confirmed: boolean) => void;
}) => {
  const week = daysWithTime(days)
    .map(day => getTrans(`jobs.filters.day.${isoDayOf(day)}` as TranslationKey))
    .join(', ');

  return (
    <Box data-testid="apply-review-step" sx={{ display: 'grid', gap: 2 }}>
      <Box>
        <Text variant="subtitle1" color="text.primary" sx={{ fontWeight: 700 }}>
          {listing.title}
        </Text>
        <Text variant="body2">
          {listing.companyName ?? listing.occupationName}
        </Text>
      </Box>
      <Box>
        <Row
          label={getTrans('jobs.detail.hourlyRate')}
          value={formatMoney(listing.hourlyRate)}
        />
        <Row
          label={getTrans('jobs.apply.week.hours')}
          value={getTransWithParams('jobs.card.hoursPerWeek', {
            hours: hoursLabel(weeklyMinutes, weeklyMinutes)
          })}
        />
        {week && (
          <Row label={getTrans('jobs.apply.review.week')} value={week} />
        )}
        {startFrom && (
          <Row
            label={getTrans('jobs.apply.week.start')}
            value={formatDay(startFrom)}
          />
        )}
      </Box>

      {needsLocation && (
        <FormControlLabel
          data-testid="apply-review-location"
          control={
            <Checkbox
              checked={locationConfirmed}
              onChange={event => onLocationConfirmed(event.target.checked)}
            />
          }
          label={getTrans('jobs.apply.review.location')}
        />
      )}

      {(listing.processSteps?.length ?? 0) > 0 && (
        <Box>
          <Text
            variant="subtitle1"
            component="h3"
            color="primary.main"
            sx={{ fontWeight: 700, mb: 1 }}
          >
            {getTrans('jobs.detail.process')}
          </Text>
          <ProcessSteps listing={listing} />
        </Box>
      )}

      <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
        {getTrans('jobs.apply.review.privacy')}
      </Alert>
    </Box>
  );
};
